import React, { useState } from 'react';
import { Phone, Mail, User, MessageSquare, Send, MapPin, Clock } from 'lucide-react';

const Form = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Message sent successfully!');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contacts = [
    { icon: <Phone className="w-4 h-4" />, label: "Phone", value: "+254 743 076376", href: "tel:+254743076376" },
    { icon: <Mail className="w-4 h-4" />, label: "Email", value: "info@torchbearer.co.ke", href: "mailto:info@torchbearer.co.ke" },
    { icon: <MapPin className="w-4 h-4" />, label: "Location", value: "Nairobi, Kenya" },
    { icon: <Clock className="w-4 h-4" />, label: "Hours", value: "Mon-Fri: 8AM - 6PM" },
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-10" style={{ background: '#fafafa' }}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-2" style={{ color: '#2a2118' }}>
            Get in Touch
          </h2>
          <p className="text-base font-light" style={{ color: '#5a524c' }}>
            We're here to help and answer any questions you might have
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">

          {/* Contact Info */}
          <div className="lg:col-span-2 rounded-2xl p-8 text-white relative overflow-hidden" style={{ background: '#712B35' }}>
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 bg-white -translate-y-1/2 translate-x-1/2" />
            <h3 className="text-base font-bold mb-6">Contact Information</h3>
            <div className="space-y-6">
              {contacts.map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.12)' }}>
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.5)' }}>{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-sm font-medium text-white hover:opacity-80 transition-opacity">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm font-medium text-white">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 border border-gray-100">
            <form onSubmit={handleSubmit}>
              <h3 className="text-base font-bold mb-5" style={{ color: '#2a2118' }}>Send a Message</h3>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: focusedField === 'name' ? '#712B35' : '#9ca3af' }} />
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange}
                      onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField('')}
                      required
                      className="w-full pl-10 pr-3.5 py-3 rounded-xl border text-sm outline-none transition-all duration-300"
                      style={{
                        borderColor: focusedField === 'name' ? '#712B35' : '#d1d5db',
                        boxShadow: focusedField === 'name' ? '0 0 0 3px rgba(113,43,53,0.08)' : 'none'
                      }}
                      placeholder="Your name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: focusedField === 'email' ? '#712B35' : '#9ca3af' }} />
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange}
                      onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField('')}
                      required
                      className="w-full pl-10 pr-3.5 py-3 rounded-xl border text-sm outline-none transition-all duration-300"
                      style={{
                        borderColor: focusedField === 'email' ? '#712B35' : '#d1d5db',
                        boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(113,43,53,0.08)' : 'none'
                      }}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Message</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4" style={{ color: focusedField === 'message' ? '#712B35' : '#9ca3af' }} />
                    <textarea
                      name="message" value={formData.message} onChange={handleChange}
                      onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField('')}
                      rows={4} required
                      className="w-full pl-10 pr-3.5 py-3 rounded-xl border text-sm outline-none transition-all duration-300 resize-none"
                      style={{
                        borderColor: focusedField === 'message' ? '#712B35' : '#d1d5db',
                        boxShadow: focusedField === 'message' ? '0 0 0 3px rgba(113,43,53,0.08)' : 'none'
                      }}
                      placeholder="How can we help you?"
                    />
                  </div>
                </div>

                <button
                  type="submit" disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all duration-300 flex items-center justify-center gap-2"
                  style={{ background: isSubmitting ? '#9ca3af' : '#712B35' }}
                  onMouseEnter={(e) => { if (!isSubmitting) e.target.style.background = '#5f2b40'; }}
                  onMouseLeave={(e) => { if (!isSubmitting) e.target.style.background = '#712B35'; }}
                >
                  {isSubmitting ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Send Message</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;
